import { useEffect, useRef, useState } from 'react';
import { usePlayerActionStore } from '@/state/playerActionStore';
import { useCharacterStore } from '@/state/characterStore';
import { useUIStore } from '@/state/uiStore';
import { useChatStore } from '@/state/chatStore';
import { Friend, Party, Trade } from '@/network/RestApi';

const POPOVER_W = 180;

export function PlayerActionPopover() {
  const target = usePlayerActionStore((s) => s.target);
  const close = usePlayerActionStore((s) => s.close);
  const ch = useCharacterStore((s) => s.current);
  const showToast = useUIStore((s) => s.showToast);
  const pushChat = useChatStore((s) => s.push);
  const setChannel = useChatStore((s) => s.setActive);
  const ref = useRef<HTMLDivElement | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!target) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [target, close]);

  if (!target || !ch) return null;

  const left = Math.min(window.innerWidth - POPOVER_W - 8, Math.max(8, target.screenX + 12));
  const top = Math.min(window.innerHeight - 220, Math.max(8, target.screenY + 8));

  const wrap = async (fn: () => Promise<void>) => {
    if (busy) return;
    setBusy(true);
    try { await fn(); } finally { setBusy(false); close(); }
  };

  const onWhisper = () => {
    setChannel('nearby');
    pushChat({ channel: 'system', from: 'system', text: `[Whisper] (chưa hỗ trợ private chat) - sẽ nhắn cho ${target.name}` });
    close();
  };

  const onAddFriend = () => wrap(async () => {
    try {
      const r = await Friend.request(ch.id, { targetName: target.name });
      showToast(r.accepted ? `Đã kết bạn với ${target.name}` : `Đã gửi lời mời tới ${target.name}`);
    } catch (e) { showToast((e as Error).message, 'error'); }
  });

  const onInviteParty = () => wrap(async () => {
    try {
      let pid: number | null = null;
      const r = await Party.ofCharacter(ch.id);
      if (r && (r as { id: number }).id) pid = (r as { id: number }).id;
      if (!pid) {
        const created = await Party.create({ leaderId: ch.id });
        pid = created.id;
      }
      await Party.invite(pid, { byCharacterId: ch.id, targetName: target.name });
      showToast(`Đã mời ${target.name} vào party`);
    } catch (e) { showToast((e as Error).message, 'error'); }
  });

  const onTrade = () => wrap(async () => {
    try {
      const r = await Trade.request(ch.id, { targetName: target.name });
      if (r.accepted) {
        showToast(`Bắt đầu giao dịch với ${target.name}`);
        useUIStore.getState().set('trade', true);
      } else {
        showToast(`Đã gửi lời mời giao dịch tới ${target.name}`);
      }
    } catch (e) { showToast((e as Error).message, 'error'); }
  });
  const onInspect = () => { showToast(`Xem nhân vật ${target.name} (sắp ra mắt)`, 'info'); close(); };

  return (
    <div
      ref={ref}
      data-block-game-input="true"
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'absolute',
        left,
        top,
        width: POPOVER_W,
        background: 'rgba(15,21,32,0.97)',
        border: '1px solid #4a6890',
        borderRadius: 6,
        boxShadow: '0 6px 18px rgba(0,0,0,0.7)',
        color: '#e8eef7',
        padding: 8,
        zIndex: 100,
        userSelect: 'none',
      }}
    >
      <div style={{
        fontWeight: 700,
        color: '#ffd166',
        marginBottom: 4,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{target.name}</span>
        <button onClick={close} style={closeBtn} aria-label="close">×</button>
      </div>
      <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 6 }}>{labelClass(target.klass)}</div>

      <Btn onClick={onWhisper}        disabled={busy}>💬  Nhắn riêng</Btn>
      <Btn onClick={onAddFriend}      disabled={busy}>🤝  Kết bạn</Btn>
      <Btn onClick={onInviteParty}    disabled={busy}>👥  Mời vào party</Btn>
      <Btn onClick={onTrade}          disabled={busy}>💱  Giao dịch</Btn>
      <Btn onClick={onInspect}        disabled={busy}>🔍  Xem nhân vật</Btn>
    </div>
  );
}

function labelClass(k: 'warrior' | 'wizard' | 'archer'): string {
  return k === 'warrior' ? 'Chiến binh' : k === 'wizard' ? 'Pháp sư' : 'Cung thủ';
}

function Btn({ children, onClick, disabled }: { children: React.ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        background: 'transparent',
        color: '#e8eef7',
        border: 'none',
        padding: '6px 8px',
        cursor: disabled ? 'wait' : 'pointer',
        borderRadius: 4,
        fontSize: 13,
      }}
      onMouseEnter={(e) => { (e.currentTarget.style.background = 'rgba(76, 209, 255, 0.18)'); }}
      onMouseLeave={(e) => { (e.currentTarget.style.background = 'transparent'); }}
    >
      {children}
    </button>
  );
}

const closeBtn: React.CSSProperties = {
  background: 'transparent',
  color: '#aaa',
  border: 'none',
  fontSize: 18,
  lineHeight: 1,
  cursor: 'pointer',
  padding: '0 4px',
};
